import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Splitter } from 'primeng/splitter';
import { AuthService } from '@brightside/core/auth';
import { ClientSessionService } from '@brighthub/uisession';
import { StoryDemoFacade } from '@brighthub/story';
import { ClientChatDemoStateService } from './client-chat-demo-state.service';
import { ClientChatSuggestionsStateService, TopicItem } from './client-chat-suggestions-state.service';
import { ClientChatSuggestionsComponent } from './client-chat-suggestions/client-chat-suggestions.component';
import { ClientChatThreadStateService } from './client-chat-thread-state.service';
import { ClientChatThreadComponent } from './client-chat-thread/client-chat-thread.component';
import { storyStepDemoIdFromKey } from './story-step-key';

const DEMO_TOAST_DETAIL =
  'This is a demo app, so the chat will not be sent anywhere. This is only to show the experience.';

const DEMO_DISCARD_DETAIL =
  'Your message has been discarded — this is a demo and messages are not sent. The story is advancing to the next step.';

/** Total typewriter animation duration in ms. */
const TYPEWRITER_TOTAL_MS = 450;

@Component({
  selector: 'bai-client-chat-demo',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    Button,
    Splitter,
    ClientChatSuggestionsComponent,
    ClientChatThreadComponent,
  ],
  templateUrl: './client-chat-demo.component.html',
  styleUrl: './client-chat-demo.component.scss',
})
export class ClientChatDemoComponent {
  @ViewChild(ClientChatThreadComponent) private chatThread?: ClientChatThreadComponent;

  private readonly stateService = inject(ClientChatDemoStateService);
  private readonly suggestionsState = inject(ClientChatSuggestionsStateService);
  private readonly threadState = inject(ClientChatThreadStateService);
  private readonly clientSession = inject(ClientSessionService);
  private readonly messageService = inject(MessageService);
  private readonly auth = inject(AuthService);
  private readonly storyFacade = inject(StoryDemoFacade);

  /** Optional session id override; otherwise uses ClientSessionService.selectedSession. */
  sessionId = input<string | null>(null);

  sessionIdToUse = computed(() => {
    const over = this.sessionId();
    if (over) return over;
    return this.clientSession.selectedSession()?.uiSessionId ?? null;
  });

  /** Logged-in user first name (kept for potential future use). */
  userFirstName = signal('You');

  /** Full name of the selected client, used as the display name in the chat thread. */
  clientFullName = computed(() => {
    const s = this.clientSession.selectedSession()?.subjectData;
    if (!s) return 'Client';
    const first = s.firstName?.trim() ?? '';
    const last = s.lastName?.trim() ?? '';
    return [first, last].filter(Boolean).join(' ') || 'Client';
  });

  /** Current text being animated into the composer by the state service. */
  readonly composerDraft = computed(() => {
    const id = this.sessionIdToUse();
    if (!id) return '';
    // Access the full state signal so this computed re-evaluates on every state update.
    this.threadState.state();
    return this.threadState.composerDraft(id);
  });

  /** True while the state service is typewriter-animating a client message. */
  readonly composerTyping = computed(() => {
    const id = this.sessionIdToUse();
    if (!id) return false;
    this.threadState.state();
    return this.threadState.composerTyping(id);
  });

  /** True for the brief "send flash" moment — drives the button active style. */
  readonly composerSending = computed(() => {
    const id = this.sessionIdToUse();
    if (!id) return false;
    this.threadState.state();
    return this.threadState.composerSending(id);
  });

  constructor() {
    afterNextRender(() => {
      this.auth.getUser().then((user) => {
        const profile = user as { given_name?: string; family_name?: string };
        const given = profile.given_name?.trim() ?? '';
        const family = profile.family_name?.trim() ?? '';
        this.userFirstName.set([given, family].filter(Boolean).join(' ') || given || 'You');
      });
      // Wire scroll callback so the state service can trigger scroll-to-bottom
      // during typewriter animation without needing a direct component reference.
      this.threadState.scrollToBottomFn = () => this.chatThread?.scrollToBottom();
      // Wire composer clear so the state service can reset the real draft store
      // when a simulated send completes or a reset occurs.
      this.threadState.composerClearFn = () => {
        const id = this.sessionIdToUse();
        if (id) this.stateService.clearDraft(id);
      };
    });

    // When the story payload changes, load topics + thread from it.
    // Read all signals first to establish reactive dependencies, then write
    // inside untracked() to prevent the state mutation from re-triggering the effect.
    effect(() => {
      const sessionId = this.sessionIdToUse();
      const isAvailable = this.storyFacade.isAvailable();
      const mode = this.storyFacade.mode();
      const payload = this.storyFacade.currentPayload();
      const demoId = this.storyFacade.demoId();
      const confirmedStep = this.storyFacade.confirmedStep();
      const isStoryDemoMode = isAvailable && mode === 'story-demo';
      const hasStoryEnvelope =
        payload != null &&
        typeof payload === 'object' &&
        ('snapshot' in payload || 'delta' in payload);

      untracked(() => {
        if (!sessionId) {
          return;
        }

        // New demo selected (or async reload) clears the facade payload before the
        // next envelope arrives. Without this branch, the prior demo's thread would
        // stay visible because the "no envelope" guard below would no-op.
        const priorStoryDemo = storyStepDemoIdFromKey(this.threadState.getActiveStepKey(sessionId));
        const shouldResetForDemoChange =
          isStoryDemoMode &&
          !hasStoryEnvelope &&
          priorStoryDemo !== null &&
          priorStoryDemo !== demoId;
        if (shouldResetForDemoChange) {
          this.stateService.clearDraft(sessionId);
          this.suggestionsState.loadFallback(sessionId);
          this.threadState.loadFallback(sessionId);
          return;
        }

        // During in-flight step transitions (jump/reset/rapid navigation),
        // keep rendering the prior step until a new payload envelope lands.
        if (isStoryDemoMode && !hasStoryEnvelope) {
          return;
        }

        // Suggestions (topic chips)
        if (isStoryDemoMode) {
          const stepKey = `${demoId}:${confirmedStep}`;
          this.suggestionsState.applyStepPayload(sessionId, payload, stepKey);
        } else {
          this.stateService.clearDraft(sessionId);
          this.suggestionsState.loadFallback(sessionId);
        }

        // Chat thread — pass the full payload envelope so the adapter can
        // extract snapshot['client.chat.thread'] and delta['client.chat.thread'].
        if (isStoryDemoMode) {
          const stepKey = `${demoId}:${confirmedStep}`;
          this.threadState.applyStepPayload(sessionId, payload, stepKey);
        } else {
          this.threadState.loadFallback(sessionId);
        }
      });
    });
  }

  /**
   * What the textarea shows: the animated composer draft takes precedence
   * while the state service is running a typewriter sequence; otherwise
   * the FA's own typed draft is shown.
   */
  draft = computed(() => {
    const id = this.sessionIdToUse();
    if (!id) return '';
    // Access state signal for reactivity
    this.threadState.state();
    const animating = this.threadState.composerTyping(id) || this.threadState.composerSending(id);
    if (animating) return this.threadState.composerDraft(id);
    return this.stateService.drafts()[id] ?? '';
  });

  onInput(value: string): void {
    const id = this.sessionIdToUse();
    if (!id) return;
    // Block manual edits while the composer is being animated
    if (this.composerTyping() || this.composerSending()) return;
    this.stateService.setDraft(id, value);
    if (value === '') {
      this.suggestionsState.clearSelection(id);
    }
  }

  /**
   * Called when the FA clicks a topic chip.
   * Clears the draft, then typewriter-animates the topic's draft text into the composer.
   */
  onTopicSelected(topic: TopicItem): void {
    const id = this.sessionIdToUse();
    if (!id) return;

    // Clear any existing draft first.
    this.stateService.setDraft(id, '');

    const text = topic.draft;
    if (!text) {
      this.suggestionsState.finishGenerating(id);
      return;
    }

    let i = 0;
    const intervalMs = Math.max(8, Math.ceil(TYPEWRITER_TOTAL_MS / text.length));
    const timer = setInterval(() => {
      i++;
      this.stateService.setDraft(id, text.slice(0, i));
      if (i >= text.length) {
        clearInterval(timer);
        this.suggestionsState.finishGenerating(id);
      }
    }, intervalMs);
  }

  handleEnter(event: Event): void {
    const e = event as KeyboardEvent;
    if (e.isComposing || e.key === 'Process') {
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.send();
    }
  }

  send(): void {
    const id = this.sessionIdToUse();
    const currentDraft = id ? (this.stateService.drafts()[id] ?? '') : '';
    const hasText = currentDraft.trim().length > 0;

    if (id) {
      this.stateService.clearDraft(id);
      // After sending, clear the chip selection so the FA starts fresh.
      this.suggestionsState.clearSelection(id);
    }

    // When the story service is available, advance the demo story so the
    // experience feels like the message triggered a real response.
    if (this.storyFacade.isAvailable() && !this.storyFacade.isLoading()) {
      // If the FA had typed something, let them know it was discarded in demo mode.
      if (hasText) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Message discarded (demo)',
          detail: DEMO_DISCARD_DETAIL,
          life: 6000,
        });
      }
      this.storyFacade.stepForward();
    } else {
      // Story service not configured — show the standard demo notice.
      this.messageService.add({
        severity: 'info',
        summary: 'Demo',
        detail: DEMO_TOAST_DETAIL,
        life: 6000,
      });
    }
  }

  scrollThreadToBottom(): void {
    this.chatThread?.scrollToBottom();
  }

}
